/**
 * GeoJSON builders and color utilities for map layer data.
 * No imperative MapLibre calls — this module only produces data structures
 * consumed by the declarative <Source>/<Layer>/<Marker> JSX in Map.tsx.
 */

import type {
  Feature,
  FeatureCollection,
  LineString,
  Point,
  Polygon,
} from 'geojson';
import type { Message, TeamPosition, Prediction } from '../types/api';

// ── GeoJSON builders ──────────────────────────────────────────────────────────

/**
 * Builds a FeatureCollection with a single LineString from message coordinates.
 * Returns an empty collection if fewer than 2 messages are available.
 * @param messages - array of telemetry messages
 */
export function buildTrajectoryLine(messages: Message[]): FeatureCollection<LineString> {
  if (messages.length < 2) {
    return { type: 'FeatureCollection', features: [] };
  }
  const feature: Feature<LineString> = {
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: messages.map((m) => [m.longitude, m.latitude]),
    },
    properties: {},
  };
  return { type: 'FeatureCollection', features: [feature] };
}

/**
 * Builds a FeatureCollection of Point features from messages.
 * Each feature carries `msgIdx` for click-to-panel navigation.
 * @param messages - array of telemetry messages
 */
export function buildTrajectoryPoints(messages: Message[]): FeatureCollection<Point> {
  return {
    type: 'FeatureCollection',
    features: messages.map((m, idx) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [m.longitude, m.latitude] },
      properties: { msgIdx: idx },
    })),
  };
}

/**
 * Builds a single-point FeatureCollection at the given coordinates.
 * @param lng - longitude
 * @param lat - latitude
 */
export function buildSinglePoint(lng: number, lat: number): FeatureCollection<Point> {
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [lng, lat] },
        properties: {},
      },
    ],
  };
}

/**
 * Builds a Polygon FeatureCollection from an array of [lng, lat] coordinate pairs.
 * Returns empty if `points` is falsy or has fewer than 3 vertices.
 * @param points - array of [longitude, latitude] pairs forming the polygon ring
 */
export function buildPolygon(points: number[][] | undefined): FeatureCollection<Polygon> {
  if (!points || points.length < 3) {
    return { type: 'FeatureCollection', features: [] };
  }
  const ring = [...points];
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) {
    ring.push(first);
  }
  const feature: Feature<Polygon> = {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [ring] },
    properties: {},
  };
  return { type: 'FeatureCollection', features: [feature] };
}

/**
 * Builds a heatmap FeatureCollection from [lng, lat] pairs.
 * Returns empty if `points` is falsy or empty.
 * ⚠️ Callers must null-check before passing; do not render the Source/Layer pair
 * if this returns an empty collection.
 * @param points - array of [longitude, latitude] heatmap sample points
 */
export function buildHeatmap(points: number[][] | undefined): FeatureCollection<Point> {
  if (!points || points.length === 0) {
    return { type: 'FeatureCollection', features: [] };
  }
  return {
    type: 'FeatureCollection',
    features: points.map(([lng, lat]) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [lng, lat] },
      properties: {},
    })),
  };
}

/**
 * Builds a teams FeatureCollection, carrying `name` and `timestamp` as properties.
 * Skips entries with null coordinates.
 * @param teams - array of team terminal positions
 */
export function buildTeams(teams: TeamPosition[]): FeatureCollection<Point> {
  return {
    type: 'FeatureCollection',
    features: teams
      .filter((t) => t.latitude !== null && t.longitude !== null)
      .map((t) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [t.longitude!, t.latitude!] },
        properties: { name: t.terminal_name, timestamp: t.timestamp },
      })),
  };
}

// ── Color helpers ─────────────────────────────────────────────────────────────

/**
 * Converts a hex color to a CSS `rgba(...)` string with the given alpha.
 * @param hex   - 6-digit hex color (with or without `#`)
 * @param alpha - opacity in [0, 1]
 */
export function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * Builds the MapLibre `heatmap-color` expression for a given flight color.
 * Uses a density-based gradient from transparent to the flight color.
 * @param color - hex color of the flight
 */
export function heatmapColorExpr(color: string): unknown[] {
  return [
    'interpolate',
    ['linear'],
    ['heatmap-density'],
    0,   'rgba(0,0,0,0)',
    0.2, hexToRgba(color, 0.3),
    0.5, hexToRgba(color, 0.6),
    1,   color,
  ];
}

// ── Re-export Prediction type for consumers ───────────────────────────────────

export type { Prediction };
